import { useEffect, useId, useRef, useState } from 'preact/hooks';
import type { SparklineWidget } from '../widgets';
import type { IPointBindingSource, PointHistorySample, PointQuality, PointValue } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { formatLeafValue } from '../../format/leafValue';
import { useLeafMeta } from '../LeafMetaContext';
import './SparklineRenderer.css';

interface Props {
  widget: SparklineWidget;
  source: IPointBindingSource;
}

const WINDOW_MS = 24 * 60 * 60_000; // fixed 24 h look-back per concept
const HISTORY_REFRESH_MS = 5 * 60_000; // re-pull the full window every 5 min
const SVG_W = 240;
const SVG_H = 60;
const PAD_X = 2;
const PAD_Y = 4;

interface TailSample {
  t: number;
  v: number | null;
  /** Volles Qualitaets-Alphabet, nicht eine Teilmenge davon. Eine
   *  handgeschriebene Teilmenge hier war der Grund, warum `disconnected`
   *  und `override` beim Erweitern von `PointQuality` aufgefallen sind —
   *  bei einem `string`-Feld waeren sie stillschweigend durchgerutscht. */
  q: PointQuality;
}

/**
 * Compact inline mini-chart. 24 h look-back, no axes, no interaction. Ideal
 * for a KPI-strip row of six-to-eight tiles next to their live-value numbers.
 *
 * Flow:
 *   1. On mount, pull history(valuePoint, now - 24h, now).
 *   2. Refresh the full window every 5 min so we don't drift.
 *   3. Tail via subscribe(valuePoint): push each new sample, drop anything
 *      older than 24 h.
 *
 * The stroke tone flips through neutral -> warn -> alarm based on the current
 * live sample vs. the widget's threshold config; the same thresholds are
 * drawn as translucent horizontal bands under the line.
 */
export function SparklineRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.valuePoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(widget.valuePoint);
  // Unique per document: the same widget can be on screen twice (editor
  // canvas and preview), and SVG gradient ids are global.
  const gradientId = `hv-sparkline-fill-${useId()}`;

  const [tail, setTail] = useState<TailSample[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const lastAppendedTs = useRef<number>(-Infinity);

  // History fetch + periodic refresh.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      const until = new Date();
      const since = new Date(until.getTime() - WINDOW_MS);
      try {
        const samples = await source.getHistory(
          widget.valuePoint,
          since,
          until,
          'auto',
          controller.signal,
          SVG_W,
        );
        if (cancelled) return;
        const mapped = samples
          .map(toTailSample)
          .filter((s): s is TailSample => s !== null)
          .sort((a, b) => a.t - b.t);
        setTail(mapped);
        setHistoryLoaded(true);
        if (mapped.length > 0) {
          lastAppendedTs.current = mapped[mapped.length - 1].t;
        }
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[SparklineRenderer] history fetch failed', {
          pointId: widget.valuePoint,
          err,
        });
        if (!cancelled) setHistoryLoaded(true);
      }
    };

    void load();
    const timer = window.setInterval(load, HISTORY_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      controller.abort();
    };
  }, [source, widget.valuePoint]);

  // Live tail from pointSignal.
  const current = value.value;
  useEffect(() => {
    if (!current) return;
    const sample = liveToTail(current);
    if (sample === null) return;
    if (sample.t <= lastAppendedTs.current) return;
    lastAppendedTs.current = sample.t;
    setTail((prev) => trimWindow([...prev, sample], Date.now() - WINDOW_MS));
  }, [current]);

  const stale = isDegraded(current);
  const numericCurrent = typeof current?.value === 'number' && Number.isFinite(current.value)
    ? current.value
    : null;

  const tone = classifyTone(widget, numericCurrent);

  const now = Date.now();
  const windowStart = now - WINDOW_MS;
  const drawable = tail.filter((s) => s.v !== null && Number.isFinite(s.v as number));

  const yMinRaw = drawable.length > 0 ? Math.min(...drawable.map((s) => s.v as number)) : 0;
  const yMaxRaw = drawable.length > 0 ? Math.max(...drawable.map((s) => s.v as number)) : 1;
  const bounds = paddedBounds(yMinRaw, yMaxRaw, widget);

  // The builders get the full tail: its null samples are the gaps.
  const path = drawable.length > 0
    ? buildPath(tail, windowStart, now, bounds.min, bounds.max)
    : null;
  const area = drawable.length > 0
    ? buildArea(tail, windowStart, now, bounds.min, bounds.max)
    : null;
  const unit = numericCurrent !== null ? (meta?.unit ?? null) : null;

  const zones = zoneBands(widget, bounds.min, bounds.max);

  return (
    <div class={`hv-sparkline hv-sparkline--tone-${tone}${stale ? ' hv-sparkline--stale' : ''}`}
         role="img"
         aria-label={widget.label ? `Verlauf ${widget.label}` : 'Verlauf'}>
      {widget.label && (
        <div class="hv-sparkline__head">
          <span class="hv-sparkline__caption" title={widget.label}>{widget.label}</span>
          <span class="hv-sparkline__value">
            {formatLeafValue(numericCurrent, meta)}
            {unit && <span class="hv-sparkline__unit">{unit}</span>}
          </span>
        </div>
      )}
      <div class="hv-sparkline__plot">
        <svg class="hv-sparkline__svg"
             viewBox={`0 0 ${SVG_W} ${SVG_H}`}
             preserveAspectRatio="none">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="currentColor" stop-opacity="0.35" />
              <stop offset="1" stop-color="currentColor" stop-opacity="0" />
            </linearGradient>
          </defs>
          {zones.map((z) => (
            <rect key={`${z.tone}-${z.yTop}`}
                  class={`hv-sparkline__zone hv-sparkline__zone--${z.tone}`}
                  x={0}
                  y={z.yTop}
                  width={SVG_W}
                  height={z.yBottom - z.yTop} />
          ))}
          {area && <path class="hv-sparkline__area" d={area} fill={`url(#${gradientId})`} />}
          {path && (
            <path class="hv-sparkline__line"
                  d={path}
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
          )}
        </svg>
        {drawable.length === 0 && historyLoaded && (
          <div class="hv-sparkline__empty">Keine Historie</div>
        )}
      </div>
    </div>
  );
}

// --------------- helpers ---------------

function toTailSample(sample: PointHistorySample): TailSample | null {
  const t = Date.parse(sample.timestamp);
  if (!Number.isFinite(t)) return null;
  return {
    t,
    v: sample.value != null && Number.isFinite(sample.value) ? sample.value : null,
    q: sample.quality,
  };
}

function liveToTail(v: PointValue): TailSample | null {
  const t = Date.parse(v.timestamp);
  if (!Number.isFinite(t)) return null;
  const num = typeof v.value === 'number' && Number.isFinite(v.value) ? v.value : null;
  return { t, v: num, q: v.quality };
}

function trimWindow(all: TailSample[], cutoff: number): TailSample[] {
  // Drop anything older than the cutoff; keep chronological order.
  let i = 0;
  while (i < all.length && all[i].t < cutoff) i++;
  return i === 0 ? all : all.slice(i);
}

function paddedBounds(min: number, max: number, widget: SparklineWidget): { min: number; max: number } {
  // Fold thresholds into the visible range so the zone bands actually show.
  const candidates = [min, max];
  for (const t of [widget.warnLow, widget.warnHigh, widget.alarmLow, widget.alarmHigh]) {
    if (t != null && Number.isFinite(t)) candidates.push(t);
  }
  let lo = Math.min(...candidates);
  let hi = Math.max(...candidates);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 1 };
  if (hi - lo < 1e-9) {
    // Flat line: give the axis a bit of room around the value.
    const pad = Math.max(Math.abs(lo) * 0.05, 0.5);
    return { min: lo - pad, max: hi + pad };
  }
  const pad = (hi - lo) * 0.08;
  return { min: lo - pad, max: hi + pad };
}

function classifyTone(widget: SparklineWidget, current: number | null): 'ok' | 'warn' | 'alarm' {
  if (current == null) return 'ok';
  if ((widget.alarmLow != null && current <= widget.alarmLow) ||
      (widget.alarmHigh != null && current >= widget.alarmHigh)) return 'alarm';
  if ((widget.warnLow != null && current <= widget.warnLow) ||
      (widget.warnHigh != null && current >= widget.warnHigh)) return 'warn';
  return 'ok';
}

function xOf(t: number, tStart: number, tEnd: number): number {
  if (tEnd <= tStart) return PAD_X;
  const frac = (t - tStart) / (tEnd - tStart);
  return PAD_X + Math.max(0, Math.min(1, frac)) * (SVG_W - 2 * PAD_X);
}

function yOf(v: number, yMin: number, yMax: number): number {
  if (yMax <= yMin) return SVG_H / 2;
  const frac = (v - yMin) / (yMax - yMin);
  return PAD_Y + (1 - Math.max(0, Math.min(1, frac))) * (SVG_H - 2 * PAD_Y);
}

function buildPath(
  samples: TailSample[],
  tStart: number,
  tEnd: number,
  yMin: number,
  yMax: number,
): string {
  // Step line: a value holds until the next sample (CoV semantics).
  let d = '';
  let penDown = false;
  let lastY = '';
  for (const s of samples) {
    if (s.v == null || !Number.isFinite(s.v)) {
      // Gap for missing samples so a bad-quality stretch doesn't get spanned.
      penDown = false;
      continue;
    }
    const x = xOf(s.t, tStart, tEnd).toFixed(2);
    const y = yOf(s.v, yMin, yMax).toFixed(2);
    d += penDown ? ` L ${x} ${lastY} L ${x} ${y}` : `M ${x} ${y}`;
    lastY = y;
    penDown = true;
  }
  return d;
}

/** Filled area under the line, one closed shape per unbroken run, so a gap
 *  in the data stays a gap in the fill too. */
function buildArea(
  samples: TailSample[],
  tStart: number,
  tEnd: number,
  yMin: number,
  yMax: number,
): string {
  const bottom = SVG_H.toFixed(2);
  let d = '';
  let run: string[] = [];
  let firstX = '';
  let lastX = '';
  const close = () => {
    if (run.length > 1) d += `M ${firstX} ${bottom} L ${run.join(' L ')} L ${lastX} ${bottom} Z `;
    run = [];
  };
  let lastY = '';
  for (const s of samples) {
    if (s.v == null || !Number.isFinite(s.v)) { close(); continue; }
    const x = xOf(s.t, tStart, tEnd).toFixed(2);
    const y = yOf(s.v, yMin, yMax).toFixed(2);
    if (run.length === 0) firstX = x;
    else run.push(`${x} ${lastY}`);
    lastX = x;
    lastY = y;
    run.push(`${x} ${y}`);
  }
  close();
  return d.trim();
}

interface Band { tone: 'warn' | 'alarm'; yTop: number; yBottom: number; }

function zoneBands(widget: SparklineWidget, yMin: number, yMax: number): Band[] {
  const bands: Band[] = [];
  const push = (from: number, to: number, tone: 'warn' | 'alarm') => {
    const yTop = yOf(Math.max(from, to), yMin, yMax);
    const yBottom = yOf(Math.min(from, to), yMin, yMax);
    if (yBottom - yTop > 0.5) bands.push({ tone, yTop, yBottom });
  };
  if (widget.alarmLow != null) push(yMin, widget.alarmLow, 'alarm');
  if (widget.alarmHigh != null) push(widget.alarmHigh, yMax, 'alarm');
  if (widget.warnLow != null && widget.alarmLow != null) push(widget.alarmLow, widget.warnLow, 'warn');
  else if (widget.warnLow != null) push(yMin, widget.warnLow, 'warn');
  if (widget.warnHigh != null && widget.alarmHigh != null) push(widget.warnHigh, widget.alarmHigh, 'warn');
  else if (widget.warnHigh != null) push(widget.warnHigh, yMax, 'warn');
  return bands;
}
